import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Workflow, Plus, Trash2, Play, Power, Loader2, History, Save, ChevronDown,
  Webhook, Clock, Briefcase, TrendingUp, CircleDollarSign, Mail, FormInput, MessageCircle,
  Globe, Phone, Users, Slack, Ban, Filter, X, Plug,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import ModuleDemoVideo from '../components/ModuleDemoVideo';
import { TRIGGER_PIECES, ACTION_PIECES, findPiece, pieceDisplayLabel, pieceDisplayDescription } from '../data/automationPieces';
import {
  AUTOMATION_RECIPES,
  AUTOMATION_STEPS_EXPLAINER,
  PLACEHOLDER_HINTS,
  WHAT_YOU_CAN_BUILD,
  buildFlowSummary,
  recipeToWorkflow,
  humanRunStatus,
  humanStepStatus,
  smartFieldsForStep,
} from '../data/automationGuide';

const ICON_MAP = {
  Webhook, Clock, Briefcase, TrendingUp, CircleDollarSign, Mail, FormInput, MessageCircle,
  Globe, Phone, Users, Slack, Ban, Filter,
};

const TONE = {
  violet: 'bg-violet-500/15 border-violet-500/30 text-violet-200',
  sky: 'bg-sky-500/15 border-sky-500/30 text-sky-200',
  emerald: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-200',
  amber: 'bg-amber-500/15 border-amber-500/30 text-amber-200',
  green: 'bg-green-500/15 border-green-500/30 text-green-200',
  rose: 'bg-rose-500/15 border-rose-500/30 text-rose-200',
  zinc: 'bg-zinc-500/15 border-zinc-500/30 text-zinc-200',
};

const PieceIcon = ({ piece, className = 'w-4 h-4' }) => {
  const Icon = ICON_MAP[piece?.icon] || Workflow;
  return <Icon className={className} />;
};

const randomSecret = () => `evt_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;

const isWebhookLikeTrigger = (type) => type === 'webhook' || type === 'custom_event';

const defaultTriggerConfig = (type, config = {}) => {
  if (!isWebhookLikeTrigger(type)) return { ...config };
  return {
    ...config,
    secret: config.secret || randomSecret(),
  };
};

const PiecePicker = ({ kind, onPick, onClose, replacing = false }) => {
  const pieces = kind === 'trigger' ? TRIGGER_PIECES : ACTION_PIECES;
  const title = kind === 'trigger'
    ? 'When should this run?'
    : replacing
      ? 'Replace this action with…'
      : 'What should happen next?';
  const subtitle = kind === 'trigger'
    ? 'Pick the event that starts your automation.'
    : 'Steps run top to bottom, one after another.';
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid={`piece-picker-${kind}`}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div>
            <h3 className="text-base font-semibold">{title}</h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">{subtitle}</p>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-y-auto p-4 grid grid-cols-1 md:grid-cols-2 gap-2">
          {pieces.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onPick(p)}
              data-testid={`piece-${p.id}`}
              className={`text-left rounded-lg border ${TONE[p.tone]} p-3 hover:scale-[1.01] transition-transform`}
            >
              <div className="flex items-center gap-2 mb-1">
                <PieceIcon piece={p} />
                <span className="text-sm font-semibold">{pieceDisplayLabel(p)}</span>
              </div>
              <p className="text-[11px] opacity-80">{pieceDisplayDescription(p)}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const AutomationsIntroPanel = ({ collapsed, onToggle }) => (
  <div className="rounded-2xl border border-violet-500/25 bg-violet-950/20 p-4 mb-6" data-testid="automations-intro">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between text-left gap-2"
    >
      <div>
        <h2 className="text-sm font-semibold text-violet-100">How automations work</h2>
        <p className="text-[11px] text-violet-200/70 mt-0.5">No code — just “when this happens, do that.”</p>
      </div>
      <ChevronDown className={`w-4 h-4 text-violet-300 shrink-0 transition-transform ${collapsed ? '' : 'rotate-180'}`} />
    </button>
    {!collapsed && (
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        {AUTOMATION_STEPS_EXPLAINER.map((item) => (
          <div key={item.step} className="rounded-xl border border-white/10 bg-black/20 p-3">
            <p className="text-[10px] uppercase tracking-wider text-violet-300 mb-1">Step {item.step}</p>
            <p className="text-xs font-medium text-zinc-100 mb-1">{item.title}</p>
            <p className="text-[11px] text-zinc-400 leading-relaxed">{item.body}</p>
          </div>
        ))}
      </div>
    )}
    {!collapsed && (
      <div className="mt-3 pt-3 border-t border-white/10">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Popular things people build</p>
        <ul className="flex flex-wrap gap-1.5">
          {WHAT_YOU_CAN_BUILD.map((idea) => (
            <li key={idea} className="text-[10px] px-2 py-1 rounded-full border border-zinc-700 text-zinc-400">{idea}</li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

const RecipeStarterCards = ({ onApply, disabled }) => (
  <div className="mb-4" data-testid="automation-recipes">
    <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Start from a template</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {AUTOMATION_RECIPES.map((r) => (
        <button
          key={r.id}
          type="button"
          disabled={disabled}
          onClick={() => onApply(r)}
          className="text-left rounded-xl border border-zinc-800 bg-zinc-900/60 p-3 hover:border-violet-500/40 hover:bg-violet-500/5 transition-colors disabled:opacity-50"
          data-testid={`recipe-${r.id}`}
        >
          <p className="text-sm font-medium text-zinc-100">{r.emoji} {r.title}</p>
          <p className="text-[10px] text-emerald-400/90 mt-1">When: {r.when}</p>
          <p className="text-[10px] text-sky-400/90">Then: {r.then}</p>
        </button>
      ))}
    </div>
  </div>
);

const SmartFieldButtons = ({ fields = [], onInsert }) => {
  if (!fields.length) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {fields.map((f) => (
        <button
          key={f.token}
          type="button"
          onClick={() => onInsert(f.token)}
          className="rounded-full border border-zinc-700 px-2 py-0.5 text-[10px] text-zinc-400 hover:border-violet-500/50 hover:text-violet-200"
        >
          + {f.label}
        </button>
      ))}
    </div>
  );
};

const FieldEditor = ({ field, value, onChange, agents = [], smartFields = [] }) => {
  const v = value ?? field.default ?? '';
  const insertToken = (token) => onChange(`${v || ''}${v ? ' ' : ''}${token}`);
  if (field.type === 'agent_select') {
    return (
      <>
        <select
          value={v}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-8 rounded-md bg-zinc-950 border border-zinc-700 px-2 text-xs focus:outline-none focus:border-violet-500"
          data-testid={`field-${field.key}`}
        >
          <option value="">{agents.length ? 'Choose an AI Studio agent...' : 'No AI Studio agents yet'}</option>
          {agents.map((agent) => (
            <option key={agent.id} value={agent.id}>
              {agent.name || agent.id}
            </option>
          ))}
        </select>
        {!agents.length && (
          <p className="text-[10px] text-amber-300/80 mt-1">
            Create an agent in AI Studio first, then come back and select it here.
          </p>
        )}
      </>
    );
  }
  if (field.type === 'select') {
    return (
      <select
        value={v}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-8 rounded-md bg-zinc-950 border border-zinc-700 px-2 text-xs focus:outline-none focus:border-violet-500"
        data-testid={`field-${field.key}`}
      >
        {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (field.type === 'textarea') {
    return (
      <>
        <textarea
          value={v}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          placeholder={field.placeholder}
          className="w-full text-xs bg-zinc-950 border border-zinc-700 rounded-md px-2 py-1.5 focus:outline-none focus:border-violet-500"
          data-testid={`field-${field.key}`}
        />
        <SmartFieldButtons fields={smartFields} onInsert={insertToken} />
      </>
    );
  }
  if (field.type === 'json') {
    return (
      <textarea
        value={typeof v === 'object' ? JSON.stringify(v, null, 2) : v}
        onChange={(e) => {
          try { onChange(JSON.parse(e.target.value)); } catch { onChange(e.target.value); }
        }}
        rows={3}
        placeholder={field.placeholder}
        className="w-full font-mono text-[11px] bg-zinc-950 border border-zinc-700 rounded-md px-2 py-1.5 focus:outline-none focus:border-violet-500"
        data-testid={`field-${field.key}`}
      />
    );
  }
  return (
    <>
      <input
        type={field.type === 'number' ? 'number' : 'text'}
        value={v}
        onChange={(e) => onChange(field.type === 'number' ? Number(e.target.value) : e.target.value)}
        placeholder={field.placeholder}
        className="w-full h-8 rounded-md bg-zinc-950 border border-zinc-700 px-2 text-xs focus:outline-none focus:border-violet-500"
        data-testid={`field-${field.key}`}
      />
      {field.type !== 'number' && <SmartFieldButtons fields={smartFields} onInsert={insertToken} />}
    </>
  );
};

const StepConfig = ({ piece, config, onChange, agents = [], stepIndex = 0, enableSmartFields = true }) => {
  if (!piece || piece.fields.length === 0) {
    return <p className="text-[11px] text-zinc-500 italic">This piece has no configuration.</p>;
  }
  const smartFields = enableSmartFields ? smartFieldsForStep(stepIndex) : [];
  return (
    <div className="space-y-2">
      {piece.fields.map((f) => (
        <div key={f.key}>
          <label className="block text-[11px] text-zinc-400 mb-1">
            {f.label}{f.required && <span className="text-rose-400"> *</span>}
          </label>
          <FieldEditor
            field={f}
            value={config?.[f.key]}
            onChange={(val) => onChange({ ...config, [f.key]: val })}
            agents={agents}
            smartFields={smartFields}
          />
          {f.help && <p className="text-[10px] text-zinc-500 mt-0.5">{f.help}</p>}
        </div>
      ))}
    </div>
  );
};

const StepRow = ({ step, index, onUpdate, onDelete, onChangeType, onAddBelow, defaultOpen = true, agents = [] }) => {
  const piece = findPiece(step.type);
  const [open, setOpen] = useState(defaultOpen);

  if (!piece) {
    return (
      <div className="rounded-xl border border-rose-500/30 bg-rose-950/20 p-3 text-xs" data-testid={`step-row-${index}`}>
        <p className="text-rose-300 mb-2">Unknown step type: {step.type}</p>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={onChangeType}>Choose action</Button>
          <Button size="sm" variant="outline" className="h-7 text-[10px] text-rose-400" onClick={onDelete}>Delete</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={`step-row-${index}`}>
      <div className={`rounded-xl border ${TONE[piece.tone]} bg-zinc-900/60`}>
        <div className="flex items-center justify-between p-3 gap-2">
          <button
            type="button"
            className="flex items-center gap-2 min-w-0 flex-1 text-left"
            onClick={() => setOpen(!open)}
          >
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 shrink-0">#{index + 1}</span>
            <PieceIcon piece={piece} className="w-4 h-4 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">Then: {pieceDisplayLabel(piece)}</p>
              <p className="text-[10px] text-zinc-500 truncate">
                {open ? 'Click to collapse settings' : 'Click to edit settings'}
              </p>
            </div>
            <ChevronDown className={`w-4 h-4 text-zinc-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 text-[10px] border-zinc-600"
              onClick={onChangeType}
              data-testid={`step-change-${index}`}
            >
              Change
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 text-[10px] border-rose-500/40 text-rose-300 hover:bg-rose-950/30"
              onClick={onDelete}
              data-testid={`step-delete-${index}`}
            >
              <Trash2 className="w-3 h-3 mr-0.5" /> Delete
            </Button>
          </div>
        </div>
        {open && (
          <div className="border-t border-zinc-800 p-3" data-testid={`step-config-${index}`}>
            <StepConfig
              piece={piece}
              config={step.config}
              onChange={(c) => onUpdate({ ...step, config: c })}
              agents={agents}
              stepIndex={index}
            />
            <div className="mt-3 pt-3 border-t border-zinc-800/60" data-testid={`step-reliability-${index}`}>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Reliability</p>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-[10px] text-zinc-400 mb-1">Retries</label>
                  <input type="number" min="0" max="5" value={step.config?.retry ?? 0}
                    onChange={(e) => onUpdate({ ...step, config: { ...step.config, retry: Number(e.target.value) } })}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-xs text-zinc-200" />
                </div>
                <div>
                  <label className="block text-[10px] text-zinc-400 mb-1">Backoff (s)</label>
                  <input type="number" min="0" max="30" step="0.5" value={step.config?.backoff_sec ?? 0}
                    onChange={(e) => onUpdate({ ...step, config: { ...step.config, backoff_sec: Number(e.target.value) } })}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-xs text-zinc-200" />
                </div>
                <div>
                  <label className="block text-[10px] text-zinc-400 mb-1">On error</label>
                  <select value={step.config?.on_error ?? 'stop'}
                    onChange={(e) => onUpdate({ ...step, config: { ...step.config, on_error: e.target.value } })}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-xs text-zinc-200">
                    <option value="stop">Stop workflow</option>
                    <option value="continue">Continue</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onAddBelow}
        data-testid={`step-add-below-${index}`}
        className="w-full flex items-center justify-center gap-1 py-2 text-[11px] text-violet-300 hover:text-violet-200 border border-dashed border-zinc-700 rounded-lg hover:border-violet-500/40 hover:bg-violet-500/5"
      >
        <Plus className="w-3 h-3" /> Add another action below
      </button>
    </div>
  );
};

const WorkflowEditor = ({ workflow, onChange, onSave, onTestRun, onToggleActive, saving, testRunning, runs, backendUrl, agents = [] }) => {
  const [picker, setPicker] = useState(null);
  const [insertAt, setInsertAt] = useState(null);
  const [replaceStepIndex, setReplaceStepIndex] = useState(null);
  const [showPlaceholders, setShowPlaceholders] = useState(false);

  const triggerPiece = findPiece(workflow.trigger?.type) || null;
  const flowSummary = buildFlowSummary(workflow);
  const webhookSecret = workflow.trigger?.config?.secret || 'YOUR_SECRET';
  const webhookUrl = workflow.id && isWebhookLikeTrigger(workflow.trigger?.type) && backendUrl
    ? `${backendUrl}/api/automations/webhook/${workflow.id}/${webhookSecret}`
    : null;

  const setTrigger = (p) => {
    onChange({ ...workflow, trigger: { type: p.id, config: defaultTriggerConfig(p.id) } });
    setPicker(null);
    setReplaceStepIndex(null);
  };

  const clearTrigger = () => {
    onChange({ ...workflow, trigger: null });
  };

  const addStep = (p) => {
    if (replaceStepIndex !== null) {
      const next = [...workflow.steps];
      next[replaceStepIndex] = {
        ...next[replaceStepIndex],
        type: p.id,
        config: {},
      };
      onChange({ ...workflow, steps: next });
      setReplaceStepIndex(null);
      setPicker(null);
      return;
    }
    const step = { id: crypto.randomUUID(), type: p.id, order: workflow.steps.length, config: {} };
    const at = insertAt ?? workflow.steps.length;
    const next = [...workflow.steps];
    next.splice(at, 0, step);
    onChange({ ...workflow, steps: next.map((s, i) => ({ ...s, order: i })) });
    setPicker(null);
    setInsertAt(null);
  };

  const openActionPicker = (opts = {}) => {
    setReplaceStepIndex(opts.replaceIndex ?? null);
    setInsertAt(opts.insertAt ?? null);
    setPicker('action');
  };

  const closePicker = () => {
    setPicker(null);
    setInsertAt(null);
    setReplaceStepIndex(null);
  };

  return (
    <>
      <div className="space-y-3">
        <div className="rounded-xl border border-sky-500/20 bg-sky-950/15 px-3 py-2" data-testid="workflow-flow-summary">
          <p className="text-[10px] uppercase tracking-wider text-sky-400/80 mb-0.5">Your automation in one line</p>
          <p className="text-xs text-zinc-200 leading-relaxed">{flowSummary}</p>
          <p className="text-[10px] text-zinc-500 mt-1">
            Edit below: use <strong className="text-zinc-400">Change</strong> or <strong className="text-zinc-400">Delete</strong> on the trigger and each action.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-[10px] text-zinc-500">
          <span className="inline-flex items-center gap-1 rounded-full border border-zinc-700 px-2 py-0.5">
            <Play className="w-3 h-3 text-sky-400" /> Test run = try with sample data
          </span>
          <span className="inline-flex items-center gap-1 rounded-full border border-zinc-700 px-2 py-0.5">
            <Power className="w-3 h-3 text-emerald-400" /> Live = runs on real events
          </span>
          <button
            type="button"
            onClick={() => setShowPlaceholders(!showPlaceholders)}
            className="text-violet-300 hover:text-violet-200 underline"
          >
            {showPlaceholders ? 'Hide' : 'Show'} smart text codes
          </button>
        </div>

        {showPlaceholders && (
          <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3 text-[10px] space-y-1" data-testid="placeholder-hints">
            <p className="text-zinc-400 mb-1">Paste these into email fields, Slack messages, etc. They fill in automatically from the event:</p>
            {PLACEHOLDER_HINTS.map((h) => (
              <p key={h.token} className="font-mono text-zinc-300">
                <span className="text-violet-300">{h.token}</span>
                <span className="text-zinc-500"> — {h.meaning}</span>
              </p>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between">
          <input
            value={workflow.name}
            onChange={(e) => onChange({ ...workflow, name: e.target.value })}
            data-testid="workflow-name"
            className="bg-transparent text-xl font-semibold text-white focus:outline-none border-b border-transparent focus:border-violet-500"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={onToggleActive}
              data-testid="workflow-toggle-active"
              className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[11px] font-medium border ${
                workflow.status === 'active'
                  ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200'
                  : 'bg-zinc-800 border-zinc-700 text-zinc-300'
              }`}
            >
              <Power className="w-3.5 h-3.5" /> {workflow.status === 'active' ? 'Live' : 'Paused'}
            </button>
            <Button size="sm" onClick={onTestRun} disabled={testRunning} data-testid="workflow-test-run"
              className="h-8 px-3 text-[11px] bg-sky-500/15 border border-sky-500/40 text-sky-200 hover:bg-sky-500/25"
              title="Runs your steps once using fake sample lead data — safe way to test">
              {testRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1" />}
              Test run
            </Button>
            <Button size="sm" onClick={onSave} disabled={saving} data-testid="workflow-save"
              className="h-8 px-3 text-[11px] bg-violet-600 hover:bg-violet-500">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Save
            </Button>
          </div>
        </div>

        {/* Trigger card */}
        <div className="rounded-xl border border-violet-500/30 bg-zinc-900/60 p-3" data-testid="workflow-trigger-card">
          <p className="text-[10px] uppercase tracking-wider text-violet-300 mb-1">When (trigger)</p>
          {triggerPiece ? (
            <>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <PieceIcon piece={triggerPiece} className="w-4 h-4 text-violet-300" />
                  <div>
                    <p className="text-sm font-semibold">{pieceDisplayLabel(triggerPiece)}</p>
                    <p className="text-[11px] text-zinc-500">{pieceDisplayDescription(triggerPiece)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 text-[10px] border-violet-500/40 text-violet-200"
                    onClick={() => { setReplaceStepIndex(null); setPicker('trigger'); }}
                  >
                    Change
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 text-[10px] border-rose-500/40 text-rose-300 hover:bg-rose-950/30"
                    onClick={clearTrigger}
                    data-testid="workflow-clear-trigger"
                  >
                    <Trash2 className="w-3 h-3 mr-0.5" /> Remove
                  </Button>
                </div>
              </div>
              {webhookUrl && (
                <div className="mb-2 rounded-lg border border-amber-500/25 bg-amber-950/20 p-2 text-[10px]">
                  <p className="text-amber-200/90 font-medium mb-1">Your webhook URL (POST JSON here when Live)</p>
                  <code className="block text-zinc-300 break-all">{webhookUrl}</code>
                  <p className="text-zinc-500 mt-1">Set a secret below and use the same value in the URL.</p>
                </div>
              )}
              <StepConfig
                piece={triggerPiece}
                config={workflow.trigger?.config}
                onChange={(c) => onChange({ ...workflow, trigger: { ...workflow.trigger, config: c } })}
                stepIndex={0}
                enableSmartFields={false}
              />
            </>
          ) : (
            <button type="button" onClick={() => { setReplaceStepIndex(null); setPicker('trigger'); }} data-testid="workflow-pick-trigger"
              className="w-full h-10 rounded-md border border-dashed border-violet-500/40 text-violet-300 text-xs hover:bg-violet-500/10">
              + Choose when this should run
            </button>
          )}
        </div>

        <p className="text-[10px] uppercase tracking-wider text-zinc-500 px-1">Then (actions — top to bottom)</p>

        {/* Steps */}
        <div className="space-y-2">
          {workflow.steps.map((s, i) => (
            <StepRow
              key={s.id}
              step={s}
              index={i}
              onUpdate={(n) => {
                const next = [...workflow.steps];
                next[i] = n;
                onChange({ ...workflow, steps: next });
              }}
              onDelete={() => {
                const next = workflow.steps.filter((_, j) => j !== i).map((s, idx) => ({ ...s, order: idx }));
                onChange({ ...workflow, steps: next });
              }}
              onChangeType={() => openActionPicker({ replaceIndex: i })}
              onAddBelow={() => openActionPicker({ insertAt: i + 1 })}
              agents={agents}
            />
          ))}
          <Button
            type="button"
            variant="outline"
            className="w-full h-10 border-dashed border-zinc-600 text-zinc-300 hover:border-violet-500/50 hover:bg-violet-500/10"
            onClick={() => openActionPicker({ insertAt: workflow.steps.length })}
            data-testid="workflow-add-action"
          >
            <Plus className="w-4 h-4 mr-1" /> Add action
          </Button>
          {workflow.steps.length === 0 && (
            <p className="text-[11px] text-zinc-500 text-center italic">No actions yet — click Add action to choose email, Slack, CRM, etc.</p>
          )}
        </div>

        {/* Runs */}
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-3" data-testid="workflow-runs">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold inline-flex items-center gap-2">
              <History className="w-4 h-4 text-zinc-400" /> Run history
            </p>
            <span className="text-[10px] uppercase tracking-wider text-zinc-500">{runs.length} runs</span>
          </div>
          <p className="text-[10px] text-zinc-500 mb-2">Each test or live run is logged here. Expand a row to see what each step did.</p>
          {runs.length === 0 ? (
            <p className="text-[11px] text-zinc-500 italic">No runs yet. Click Test run above to try your automation safely.</p>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-auto pr-1">
              {runs.map((r) => (
                <details key={r.id} className="rounded border border-zinc-800 bg-zinc-950/40" data-testid={`run-${r.id.slice(0, 8)}`}>
                  <summary className="cursor-pointer p-2 flex items-center justify-between text-[11px] gap-2">
                    <span className={`font-medium ${
                      r.status === 'ok' ? 'text-emerald-300' :
                      r.status === 'filtered' ? 'text-amber-300' :
                      'text-rose-300'
                    }`}>{humanRunStatus(r.status)}</span>
                    <span className="text-zinc-500 shrink-0">{new Date(r.started_at).toLocaleString()} · {r.duration_ms}ms</span>
                  </summary>
                  <div className="px-2 pb-2 space-y-1">
                    {(r.step_logs || []).map((log, idx) => (
                      <p key={`${r.id}-s-${idx}`} className="text-[10px] text-zinc-400">
                        <span className="text-zinc-300">Step {idx + 1}</span>
                        {' '}{findPiece(log.type)?.simpleLabel || log.type}:{' '}
                        <span className={log.status === 'ok' ? 'text-emerald-400' : 'text-amber-400'}>
                          {humanStepStatus(log.status)}
                        </span>
                        {log.error && <span className="text-rose-400"> — {log.error}</span>}
                      </p>
                    ))}
                    <pre className="text-[10px] text-zinc-500 p-2 overflow-x-auto whitespace-pre-wrap border-t border-zinc-800 mt-1">{JSON.stringify(r.step_logs, null, 2)}</pre>
                  </div>
                </details>
              ))}
            </div>
          )}
        </div>
      </div>

      {picker && (
        <PiecePicker
          kind={picker}
          onClose={closePicker}
          onPick={picker === 'trigger' ? setTrigger : addStep}
          replacing={picker === 'action' && replaceStepIndex !== null}
        />
      )}
    </>
  );
};

const AutomationsDashboard = () => {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testRunning, setTestRunning] = useState(false);
  const [introCollapsed, setIntroCollapsed] = useState(false);

  const token = localStorage.getItem('token');
  const API = process.env.REACT_APP_BACKEND_URL;

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }), [token]);

  const apiFetch = useCallback(async (path, options = {}) => {
    const res = await fetch(`${API}${path}`, { ...options, headers: { ...headers, ...(options.headers || {}) } });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
    return data;
  }, [API, headers]);

  const loadWorkflows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch('/api/automations/workflows');
      setWorkflows(data || []);
      if (!selectedId && data && data.length > 0) {
        setSelectedId(data[0].id);
        setDraft(data[0]);
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, selectedId]);

  const loadRuns = useCallback(async (wid) => {
    if (!wid) { setRuns([]); return; }
    try {
      const data = await apiFetch(`/api/automations/workflows/${wid}/runs?limit=20`);
      setRuns(data || []);
    } catch {/* fresh workflows have no runs */}
  }, [apiFetch]);

  useEffect(() => { if (token) loadWorkflows(); }, [token, loadWorkflows]);

  const loadAgents = useCallback(async () => {
    try {
      const data = await apiFetch('/api/ai-studio/apps');
      setAgents(Array.isArray(data) ? data : []);
    } catch {
      setAgents([]);
    }
  }, [apiFetch]);

  useEffect(() => { if (token) loadAgents(); }, [token, loadAgents]);

  const [analytics, setAnalytics] = useState(null);
  const loadAnalytics = useCallback(async () => {
    try { setAnalytics(await apiFetch('/api/automations/analytics')); } catch (e) { /* non-fatal */ }
  }, [apiFetch]);
  useEffect(() => { if (token) loadAnalytics(); }, [token, loadAnalytics]);

  useEffect(() => {
    const wf = workflows.find((w) => w.id === selectedId);
    if (wf) {
      setDraft(wf);
      loadRuns(wf.id);
    } else if (workflows.length === 0) {
      setDraft(null); setRuns([]);
    }
  }, [selectedId, workflows, loadRuns]);

  const [provisioning, setProvisioning] = useState(false);
  const provisionTriage = async () => {
    setProvisioning(true);
    try {
      const res = await apiFetch('/api/automations/triage/provision', {
        method: 'POST',
        body: JSON.stringify({ activate: true }),
      });
      toast.success(res.message || 'Triage gate ready');
      await loadWorkflows();
    } catch (e) { toast.error(e.message); }
    finally { setProvisioning(false); }
  };

  const [pebbyBusy, setPebbyBusy] = useState(false);
  const provisionPebby = async () => {
    setPebbyBusy(true);
    try {
      await apiFetch('/api/pebby/provision', { method: 'POST' });
      const ev = await apiFetch('/api/pebby/evaluate', { method: 'POST' });
      toast.success(`Retention ready — scanned ${ev.customers} customers, ${ev.churn_risk_fired} churn / ${ev.upsell_fired} upsell fired`);
      await loadWorkflows();
      await loadAnalytics();
    } catch (e) { toast.error(e.message); }
    finally { setPebbyBusy(false); }
  };

  const retrainScore = async () => {
    try {
      const r = await apiFetch('/api/crm/score-model/retrain', { method: 'POST' });
      toast.success(r.trained
        ? `Lead score retrained on ${r.n_won} won / ${r.n_lost} lost`
        : `Need more won/lost deals to retrain (have ${r.n_won} won / ${r.n_lost} lost)`);
    } catch (e) { toast.error(e.message); }
  };

  const createWorkflow = async () => {
    try {
      const data = await apiFetch('/api/automations/workflows', {
        method: 'POST',
        body: JSON.stringify({
          name: 'New workflow',
          status: 'inactive',
          trigger: { type: 'webhook', config: defaultTriggerConfig('webhook') },
          steps: [],
        }),
      });
      setWorkflows((prev) => [data, ...prev]);
      setSelectedId(data.id);
      toast.success('Workflow created');
    } catch (e) { toast.error(e.message); }
  };

  const deleteWorkflow = async (id) => {
    if (!window.confirm('Delete this workflow?')) return;
    try {
      await apiFetch(`/api/automations/workflows/${id}`, { method: 'DELETE' });
      setWorkflows((prev) => prev.filter((w) => w.id !== id));
      if (selectedId === id) { setSelectedId(null); setDraft(null); }
      toast.success('Deleted');
    } catch (e) { toast.error(e.message); }
  };

  const saveDraft = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const data = await apiFetch(`/api/automations/workflows/${draft.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: draft.name, status: draft.status, trigger: draft.trigger, steps: draft.steps,
        }),
      });
      setWorkflows((prev) => prev.map((w) => (w.id === data.id ? data : w)));
      toast.success('Saved');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    if (!draft) return;
    const nextStatus = draft.status === 'active' ? 'inactive' : 'active';
    setDraft({ ...draft, status: nextStatus });
    try {
      const data = await apiFetch(`/api/automations/workflows/${draft.id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: nextStatus }),
      });
      setWorkflows((prev) => prev.map((w) => (w.id === data.id ? data : w)));
      toast.success(nextStatus === 'active' ? 'Live — will run on real events' : 'Paused — use Test run to try safely');
    } catch (e) { toast.error(e.message); }
  };

  const applyRecipe = async (recipe) => {
    try {
      let wfId = draft?.id;
      if (!wfId) {
        const created = await apiFetch('/api/automations/workflows', {
          method: 'POST',
          body: JSON.stringify({
            name: recipe.title,
            status: 'inactive',
            trigger: recipe.trigger,
            steps: [],
          }),
        });
        wfId = created.id;
        setWorkflows((prev) => [created, ...prev]);
        setSelectedId(wfId);
      }
      const next = recipeToWorkflow(recipe, wfId);
      next.trigger = {
        ...next.trigger,
        config: defaultTriggerConfig(next.trigger?.type, next.trigger?.config),
      };
      setDraft(next);
      await apiFetch(`/api/automations/workflows/${wfId}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: next.name,
          status: next.status,
          trigger: next.trigger,
          steps: next.steps,
        }),
      });
      setWorkflows((prev) => prev.map((w) => (w.id === wfId ? { ...w, ...next } : w)));
      toast.success(`Loaded “${recipe.title}” — review steps, then Test run`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const testRun = async () => {
    if (!draft) return;
    setTestRunning(true);
    try {
      // Save first so test-run uses the latest version
      await apiFetch(`/api/automations/workflows/${draft.id}`, {
        method: 'PUT',
        body: JSON.stringify({ name: draft.name, status: draft.status, trigger: draft.trigger, steps: draft.steps }),
      });
      const result = await apiFetch(`/api/automations/workflows/${draft.id}/test-run`, {
        method: 'POST',
        body: JSON.stringify({ sample: { email: 'demo@snagcart.dev', first_name: 'Demo', company: 'Snagcart', source: 'lead_scraper' } }),
      });
      toast[result.status === 'ok' ? 'success' : 'message'](
        result.status === 'ok'
          ? `Test passed in ${result.duration_ms}ms — check Run history below`
          : `${humanRunStatus(result.status)} — expand Run history for details`,
      );
      await loadRuns(draft.id);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setTestRunning(false);
    }
  };

  return (
    <div className="min-h-screen md:pl-64 p-6 md:p-8 bg-zinc-950 text-white">
      <header className="flex items-center justify-between mb-6" data-testid="automations-header">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="p-2 rounded-full hover:bg-white/5 text-zinc-400">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
              <Workflow className="w-6 h-6 text-violet-400" />
              Snagcart Automations
            </h1>
            <p className="text-xs text-zinc-400">When something happens in your business, Snagcart can automatically email, update CRM, or notify your team.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={provisionTriage}
            disabled={provisioning}
            data-testid="automations-triage"
            title="Create lead-routing automations: low-score leads are nurtured, medium leads get outreach, high-score leads become priority."
            className="h-8 px-3 text-[11px] border-zinc-700 hover:bg-zinc-800"
          >
            {provisioning ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Workflow className="w-3.5 h-3.5 mr-1" />}
            Auto-sort new leads
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={provisionPebby}
            disabled={pebbyBusy}
            data-testid="automations-pebby"
            title="Create customer follow-up automations for churn risk and upsell opportunities."
            className="h-8 px-3 text-[11px] border-zinc-700 hover:bg-zinc-800"
          >
            {pebbyBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Workflow className="w-3.5 h-3.5 mr-1" />}
            Set up customer follow-up
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={retrainScore}
            data-testid="automations-retrain"
            title="Learn from won and lost deals so future lead scores become smarter."
            className="h-8 px-3 text-[11px] border-zinc-700 hover:bg-zinc-800"
          >
            <Workflow className="w-3.5 h-3.5 mr-1" /> Improve lead scoring
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate('/connections')}
            data-testid="automations-connections"
            title="Open Connections dashboard — configure Mailgun, Cal.com, ElevenLabs, and more"
            className="h-8 px-3 text-[11px] border-violet-500/40 text-violet-200 hover:bg-violet-500/10"
          >
            <Plug className="w-3.5 h-3.5 mr-1" /> Connections
          </Button>
          <Button size="sm" onClick={createWorkflow} data-testid="automations-new" className="h-8 px-3 text-[11px] bg-violet-600 hover:bg-violet-500">
            <Plus className="w-3.5 h-3.5 mr-1" /> New workflow
          </Button>
        </div>
      </header>

      <ModuleDemoVideo moduleKey="automations" className="mb-6" />

      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" data-testid="automations-analytics">
          {[
            { label: 'Sent', value: analytics.outreach?.sent ?? 0 },
            { label: 'Delivery', value: `${Math.round((analytics.outreach?.delivery_rate ?? 0) * 100)}%` },
            { label: 'Queued', value: analytics.queue?.queued ?? 0 },
            { label: 'Won', value: analytics.pipeline?.won ?? 0 },
            { label: 'Revenue', value: `$${(analytics.pipeline?.revenue ?? 0).toLocaleString()}` },
            { label: 'Avg LTV', value: `$${(analytics.pipeline?.avg_deal_ltv ?? 0).toLocaleString()}` },
          ].map((s) => (
            <div key={s.label} className="bg-zinc-900/60 border border-zinc-800 rounded-xl px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">{s.label}</div>
              <div className="text-lg font-semibold text-zinc-100">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <AutomationsIntroPanel collapsed={introCollapsed} onToggle={() => setIntroCollapsed((c) => !c)} />

      <RecipeStarterCards onApply={applyRecipe} disabled={loading} />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <aside className="lg:col-span-1 bg-zinc-900/60 border border-zinc-800 rounded-2xl p-3" data-testid="automations-list">
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500">Your automations</span>
            <span className="text-[10px] text-zinc-500">{workflows.length}</span>
          </div>
          {loading ? (
            <div className="text-xs text-zinc-500 flex items-center gap-2 p-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
          ) : workflows.length === 0 ? (
            <p className="text-[11px] text-zinc-500 italic p-2">No automations yet. Pick a template above or click New workflow.</p>
          ) : (
            <div className="space-y-1">
              {workflows.map((w) => (
                <div
                  key={w.id}
                  className={`group flex items-center justify-between rounded-md px-2 py-1.5 cursor-pointer ${
                    selectedId === w.id ? 'bg-violet-500/15 border border-violet-500/30' : 'hover:bg-zinc-800/60 border border-transparent'
                  }`}
                  onClick={() => setSelectedId(w.id)}
                  data-testid={`workflow-item-${w.id.slice(0, 8)}`}
                >
                  <div className="min-w-0">
                    <p className="text-xs font-medium truncate">{w.name}</p>
                    <p className="text-[10px] text-zinc-500 truncate">
                      <span className={w.status === 'active' ? 'text-emerald-300' : 'text-zinc-500'}>
                        {w.status === 'active' ? '● Live' : '○ Paused'}
                      </span>
                      {' · '}
                      {buildFlowSummary(w).slice(0, 72)}{buildFlowSummary(w).length > 72 ? '…' : ''}
                    </p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteWorkflow(w.id); }}
                    className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-rose-400"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </aside>

        <section className="lg:col-span-3" data-testid="automations-editor">
          {draft ? (
            <WorkflowEditor
              workflow={draft}
              onChange={setDraft}
              onSave={saveDraft}
              onTestRun={testRun}
              onToggleActive={toggleActive}
              saving={saving}
              testRunning={testRunning}
              runs={runs}
              backendUrl={API}
              agents={agents}
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-24 text-center text-zinc-500">
              <Workflow className="w-12 h-12 mb-3 opacity-40" />
              <p className="text-sm">Select an automation on the left, or start from a template above.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AutomationsDashboard;
